/**
 * OIDC Provider 的共享契约。
 *
 * 端点路径写在这里，Worker 用它们注册路由，discovery 文档也由同一份常量生成 ——
 * 避免「文档里写的地址」和「实际注册的地址」漂移。
 */

export const OIDC_PATHS = {
  discovery: '/.well-known/openid-configuration',
  jwks: '/.well-known/jwks.json',
  authorize: '/authorize',
  token: '/token',
  userinfo: '/userinfo',
  revoke: '/revoke',
} as const;

/** 支持的 scope。`openid` 之外的都是可选声明。 */
export const OIDC_SCOPES = ['openid', 'profile', 'email', 'org'] as const;
export type OidcScope = (typeof OIDC_SCOPES)[number];

export function isOidcScope(value: string): value is OidcScope {
  return (OIDC_SCOPES as readonly string[]).includes(value);
}

/** 把 scope 字符串切成数组，去重并只保留认得的项。 */
export function parseScopes(value: string): OidcScope[] {
  const out: OidcScope[] = [];
  for (const raw of value.split(/\s+/)) {
    const scope = raw.trim();
    if (scope !== '' && isOidcScope(scope) && !out.includes(scope)) out.push(scope);
  }
  return out;
}

export function scopeAllows(scopes: readonly OidcScope[], scope: OidcScope): boolean {
  return scopes.includes(scope);
}

/** id_token 里固定出现的声明。`org` / `org_role` 需要 `org` scope。 */
export const REQUIRED_ID_TOKEN_CLAIMS = ['iss', 'sub', 'aud', 'exp', 'iat'] as const;

/** PKCE 目前只接受 S256。`plain` 等于不设防。 */
export const SUPPORTED_CODE_CHALLENGE_METHODS = ['S256'] as const;
export type CodeChallengeMethod = (typeof SUPPORTED_CODE_CHALLENGE_METHODS)[number];

export function isCodeChallengeMethod(value: unknown): value is CodeChallengeMethod {
  return typeof value === 'string' && (SUPPORTED_CODE_CHALLENGE_METHODS as readonly string[]).includes(value);
}

/** RFC 7636 允许的 verifier 长度区间。 */
export const CODE_VERIFIER_MIN = 43;
export const CODE_VERIFIER_MAX = 128;

const CODE_VERIFIER_RE = /^[A-Za-z0-9\-._~]{43,128}$/;
const CODE_CHALLENGE_RE = /^[A-Za-z0-9\-._~]{43,128}$/;

export function isValidCodeVerifier(value: unknown): value is string {
  return typeof value === 'string' && CODE_VERIFIER_RE.test(value);
}

export function isValidCodeChallenge(value: unknown): value is string {
  return typeof value === 'string' && CODE_CHALLENGE_RE.test(value);
}

/** 授权码 / 刷新令牌的随机值长度（字节）。 */
export const OPAQUE_TOKEN_BYTES = 32;

// ------------------------------- 编解码 ------------------------------------

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function utf8ToBase64Url(text: string): string {
  return bytesToBase64Url(new TextEncoder().encode(text));
}

const encoder = new TextEncoder();

/**
 * 计算 PKCE 的 `code_challenge`：BASE64URL(SHA-256(ASCII(code_verifier)))，不带填充。
 * 浏览器与 Workers 都有 WebCrypto，因此这一份两边通用。
 */
export async function deriveCodeChallenge(codeVerifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(codeVerifier));
  return bytesToBase64Url(new Uint8Array(digest));
}

/** 定长比较，避免用响应时间侧信道猜授权码。 */
export function timingSafeEqualText(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let index = 0; index < a.length; index += 1) {
    diff |= a.charCodeAt(index) ^ b.charCodeAt(index);
  }
  return diff === 0;
}

/** 生成不透明的随机令牌（授权码 / 刷新令牌）。 */
export function randomOpaqueToken(): string {
  const bytes = new Uint8Array(OPAQUE_TOKEN_BYTES);
  crypto.getRandomValues(bytes);
  return bytesToBase64Url(bytes);
}

/**
 * 校验授权请求的 response_type。
 * 只支持 `code`：implicit 与 hybrid 都会把令牌暴露在 URL 里。
 */
export function isSupportedResponseType(value: unknown): value is 'code' {
  return value === 'code';
}
